import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { BaseModal } from '../ui/BaseModal';
import { DueDateField } from './DueDateField';
import { useGetCompanyValidators } from '../../services/users.service';
import { useSendForValidation } from '../../services/documents.service';

interface SendForValidationModalProps {
  companyId: string;
}

export interface SendForValidationModalRef {
  open: (documentId: string, documentName: string) => void;
}

export const SendForValidationModal = forwardRef<
  SendForValidationModalRef,
  SendForValidationModalProps
>(({ companyId }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [selectedValidatorIds, setSelectedValidatorIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');

  const { data: validators, isLoading: isLoadingValidators } = useGetCompanyValidators(companyId);
  const sendForValidationMutation = useSendForValidation(companyId);

  useImperativeHandle(ref, () => ({
    open: (docId: string, docName: string) => {
      setDocumentId(docId);
      setDocumentName(docName);
      setSelectedValidatorIds([]);
      setDueDate('');
      setIsOpen(true);
    },
  }));

  useEffect(() => {
    if (!isOpen) {
      setSelectedValidatorIds([]);
      setDueDate('');
      setDocumentId(null);
      setDocumentName('');
    }
  }, [isOpen]);

  const handleValidatorToggle = (validatorId: string) => {
    setSelectedValidatorIds((prev) =>
      prev.includes(validatorId) ? prev.filter((id) => id !== validatorId) : [...prev, validatorId],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentId || selectedValidatorIds.length === 0) return;

    sendForValidationMutation.mutate(
      {
        documentId,
        validatorIds: selectedValidatorIds,
        ...(dueDate ? { dueDate } : {}),
      },
      { onSuccess: handleClose },
    );
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const isLoading = sendForValidationMutation.isPending;
  const canSubmit = selectedValidatorIds.length > 0 && !isLoading;

  const footer = (
    <div className="flex gap-4">
      <button
        type="button"
        onClick={handleClose}
        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
        disabled={isLoading}
      >
        {t('documents.send_for_validation_modal.cancel')}
      </button>
      <button
        type="submit"
        form="send-for-validation-form"
        className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
        disabled={!canSubmit}
      >
        {isLoading && <CircleNotchIcon size={16} className="animate-spin" weight="bold" />}
        {t('documents.send_for_validation_modal.submit')}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('documents.send_for_validation_modal.title')}
      footer={footer}
      maxWidth="md"
    >
      <form
        id="send-for-validation-form"
        onSubmit={handleSubmit}
        className="flex flex-1 min-h-0 flex-col gap-6"
      >
        <div className="flex-shrink-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('documents.send_for_validation_modal.document_name')}
          </label>
          <p className="text-gray-900 font-medium">{documentName}</p>
        </div>

        <DueDateField
          id="send-for-validation-due-date"
          label={t('documents.send_for_validation_modal.due_date')}
          value={dueDate}
          onChange={setDueDate}
        />

        <div className="flex flex-1 min-h-0 flex-col">
          <label className="block flex-shrink-0 text-sm font-medium text-gray-700 mb-3">
            {t('documents.send_for_validation_modal.validators.label')}
          </label>
          <p className="flex-shrink-0 text-sm text-gray-500 mb-4">
            {t('documents.send_for_validation_modal.validators.description')}
          </p>

          {isLoadingValidators ? (
            <div className="text-center py-4 text-gray-500">{t('common.loading')}</div>
          ) : !validators || validators.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              {t('documents.send_for_validation_modal.validators.empty')}
            </div>
          ) : (
            <div className="flex-1 min-h-0 space-y-3 overflow-y-auto">
              {validators.map((validator) => (
                <label
                  key={validator.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition"
                >
                  <input
                    type="checkbox"
                    checked={selectedValidatorIds.includes(validator.id)}
                    onChange={() => handleValidatorToggle(validator.id)}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {validator.firstName} {validator.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{validator.email}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {selectedValidatorIds.length > 0 && (
          <p className="flex-shrink-0 text-sm text-gray-600">
            {t('documents.send_for_validation_modal.validators.selected', {
              count: selectedValidatorIds.length,
            })}
          </p>
        )}
      </form>
    </BaseModal>
  );
});

SendForValidationModal.displayName = 'SendForValidationModal';
